import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Comment, Answer, Question, VoteData, Notification, NotificationType } from '../types';
import useUserContext from './useUserContext';
import addComment from '../services/commentService';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  setQuestionID,
  fetchQuestion,
  questionSelector,
  updateAPQuestion,
  updateAPAnswer,
  addAnswer,
  updateVotes,
} from '../redux/slices/answersSlice';
import { setHeaderTitle } from '../redux/slices/layoutSlice';
import useAchievementTracking from './useAchievementTracking';
import { setCurrUser } from '../redux/slices/userSlice';
import addNotification from '../services/notificationService';
import { getUserOverallScore } from '../components/main/explorePage/exploreUtils';

/**
 * Custom hook for managing the answer page's state, navigation, and real-time updates.
 *
 * @returns questionID - The current question ID retrieved from the URL parameters.
 * @returns question - The current question object with its answers, comments, and votes.
 * @returns handleNewComment - Function to handle the submission of a new comment to a question or answer.
 * @returns handleNewAnswer - Function to navigate to the "New Answer" page
 */
const useAnswerPage = () => {
  const { qid } = useParams();
  const navigate = useNavigate();

  const { user, socket } = useUserContext();

  const dispatch = useAppDispatch();
  const question = useAppSelector(questionSelector) || null;

  const [answers, setAnswers] = useState<Answer[]>([]);
  const [orderBy, setOrderBy] = useState('Most Recent');
  const orderByTypes = ['Most Recent', 'User Ranking'];

  useEffect(() => {
    dispatch(setHeaderTitle('Answers'));
  }, [dispatch]);

  const { addAchievementIfEarned } = useAchievementTracking({
    achievementType: 'comment',
  });

  /**
   * Function to sort answers based on the selected order.
   * @param orderByValue the selected order.
   * @param answersToSort the answers to sort.
   * @returns the sorted answers.
   */
  const sortAnswers = (orderByValue: string, answersToSort: Answer[]) => {
    if (orderByValue === 'Most Recent') {
      return [...answersToSort].sort(
        (a, b) => new Date(b.ansDateTime).getTime() - new Date(a.ansDateTime).getTime(),
      );
    }
    return [...answersToSort].sort((a, b) => {
      if (a.ansByUser === null && b.ansByUser === null) {
        return 0; // Keep the relative order of nulls if necessary
      }
      if (a.ansByUser === null) {
        return 1; // Move a down if a.ansByUser is null
      }
      if (b.ansByUser === null) {
        return -1; // Move b down if b.ansByUser is null
      }
      return getUserOverallScore(b.ansByUser) - getUserOverallScore(a.ansByUser);
    });
  };

  /**
   * Function to handle the change of the order of the answers.
   * @param event the event that triggered the change.
   * @param newOrder the new order.
   */
  const handleOrderChange = (event: React.MouseEvent<HTMLElement>, newOrder: string) => {
    if (newOrder === null) {
      return;
    }
    setOrderBy(newOrder);
    const sortedAnswers = sortAnswers(newOrder, answers);
    setAnswers(sortedAnswers);
  };

  useEffect(() => {
    if (question) {
      setAnswers(question.answers);
    }
  }, [question]);

  /**
   * Function to handle navigation to the "New Answer" page.
   */
  const handleNewAnswer = () => {
    navigate(`/new/answer/${qid}`);
  };

  useEffect(() => {
    if (!qid) {
      navigate('/home');
      return;
    }
    dispatch(fetchQuestion({ questionID: qid, username: user.username }));
    dispatch(setQuestionID(qid));
  }, [dispatch, qid, user.username, navigate]);

  /**
   * Function to handle the submission of a new comment to a question or answer.
   *
   * @param comment - The comment object to be added.
   * @param targetType - The type of target being commented on, either 'question' or 'answer'.
   * @param targetId - The ID of the target being commented on.
   */
  const handleNewComment = async (
    comment: Comment,
    targetType: 'question' | 'answer',
    targetId: string | undefined,
  ) => {
    try {
      if (targetId === undefined) {
        throw new Error('No target ID provided.');
      }

      await addComment(targetId, targetType, comment);

      const notifToSenderAndFollowers: Notification = {
        message:
          targetType === 'question'
            ? `{sender} commented on a question`
            : `{sender} commented on an answer`,
        sender: user,
        receiver: user,
        notificationType: NotificationType.Comment,
        notificationDateTime: new Date(),
        path: `/question/${qid}`,
      };
      addNotification(notifToSenderAndFollowers);

      await addAchievementIfEarned(
        user,
        user,
        achievement => achievement.goal - achievement.progress === 1,
      );

      dispatch(setCurrUser({ identifier: user._id || '', idType: '_id' }));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error adding comment:', error);
    }
  };

  useEffect(() => {
    /**
     * Function to handle updates to the answers of a question.
     *
     * @param answer - The updated answer object.
     */
    const handleAnswerUpdate = ({ qid: id, answer }: { qid: string; answer: Answer }) => {
      dispatch(addAnswer({ id, answer }));
    };

    /**
     * Function to handle updates to the comments of a question or answer.
     *
     * @param result - The updated question or answer object.
     * @param type - The type of the object being updated, either 'question' or 'answer'.
     */
    const handleCommentUpdate = ({
      result,
      type,
    }: {
      result: Question | Answer;
      type: 'question' | 'answer';
    }) => {
      if (type === 'question') {
        dispatch(updateAPQuestion(result as Question));
      } else if (type === 'answer') {
        dispatch(updateAPAnswer(result as Answer));
      }
    };

    /**
     * Function to handle updates to the views of a question.
     *
     * @param q The updated question object.
     */
    const handleViewsUpdate = (q: Question) => {
      dispatch(updateAPQuestion(q));
    };

    /**
     * Function to handle vote updates for a question.
     *
     * @param voteData - The updated vote data for a question
     */
    const handleVoteUpdate = (voteData: VoteData) => {
      if (voteData.qid === qid) dispatch(updateVotes(voteData));
    };

    socket.on('answerUpdate', handleAnswerUpdate);
    socket.on('viewsUpdate', handleViewsUpdate);
    socket.on('commentUpdate', handleCommentUpdate);
    socket.on('voteUpdate', handleVoteUpdate);

    return () => {
      socket.off('answerUpdate', handleAnswerUpdate);
      socket.off('viewsUpdate', handleViewsUpdate);
      socket.off('commentUpdate', handleCommentUpdate);
      socket.off('voteUpdate', handleVoteUpdate);
    };
  }, [dispatch, qid, socket]);

  return {
    question,
    questionID: qid,
    orderBy,
    answers,
    orderByTypes,
    handleNewComment,
    handleNewAnswer,
    handleOrderChange,
    sortAnswers,
  };
};

export default useAnswerPage;
